import { useCallback, useEffect, useRef, useState } from 'react'
import { getLatestInstagramReels } from '@domain/features/instagram-reels/instagram-reels-feature'
import type { InstagramReel } from '@domain/models/instagram-reel'
import { getContentRefreshPolicy } from '@presentation/hooks/content-refresh-policy'
import { useAutoRefreshOnForeground } from '@presentation/hooks/useAutoRefreshOnForeground'
import { useSsrPageData } from '@presentation/ssr/useSsrPageData'

export function useHomeInstagramReels(count = 3) {
  const ssrReels = useSsrPageData()?.home?.latestInstagramReels?.slice(0, count)
  const [reels, setReels] = useState<InstagramReel[]>(ssrReels ?? [])
  const [loading, setLoading] = useState(!ssrReels)
  const [error, setError] = useState<string | null>(null)
  const requestIdRef = useRef(0)
  const refreshPolicy = getContentRefreshPolicy('home')

  const {
    triggerRefresh,
    lastSuccessAt,
    isRefreshing,
  } = useAutoRefreshOnForeground({
    refresh: async ({ reason }) => {
      const requestId = requestIdRef.current + 1
      requestIdRef.current = requestId
      const showInitialLoading = reason === 'mount' && reels.length === 0

      if (showInitialLoading) {
        setLoading(true)
      }

      try {
        setError(null)
        const result = await getLatestInstagramReels(count)
        if (requestId === requestIdRef.current) {
          setReels(result)
        }
      } catch (loadError) {
        if (requestId === requestIdRef.current) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Failed to load Instagram content.',
          )
        }
        throw loadError
      } finally {
        if (showInitialLoading && requestId === requestIdRef.current) {
          setLoading(false)
        }
      }
    },
    staleAfterMs: refreshPolicy.staleAfterMs,
    pollIntervalMs: refreshPolicy.pollIntervalMs,
    minRefreshGapMs: refreshPolicy.minRefreshGapMs,
    maxFailureBackoffMs: refreshPolicy.maxFailureBackoffMs,
  })

  useEffect(() => {
    void triggerRefresh('mount', { force: true })
  }, [triggerRefresh])

  const refresh = useCallback(async () => {
    await triggerRefresh('manual', { force: true })
  }, [triggerRefresh])

  return {
    reels,
    loading,
    error,
    refresh,
    lastUpdatedAt: lastSuccessAt,
    isRefreshing,
  }
}
